import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from "react";
import CompetitorCompany from "../api/models/product/CompetitorCompany";
import CompetitorProduct from "../api/models/product/CompetitorProduct";
import { Laboratory } from "../api/models/product/Laboratory";
import { TesaPrototype } from "../api/models/product/TesaPrototype";
import ProductReferenceResponse from "../api/models/ProductReferenceResponse";

type UnknownProduct = ProductRaw.Schemas.UnknownProductResponse;

export interface ProductContextType {
  competitorProduct: CompetitorProduct | null;
  productReference: ProductReferenceResponse | null;
  testRequest: {};
  unknownProduct: UnknownProduct | null;
  updateUnknownProduct: Dispatch<SetStateAction<UnknownProduct | null>>;
  setCompetitorProduct: Dispatch<SetStateAction<CompetitorProduct | null>>;
}

export const ProductContext = createContext<any & ProductContextType>({
  competitorProduct: null,
  productNart: null,
  testRequest: {},
  unknownProduct: null,
  updateUnknownProduct: () => {},
  setCompetitorProduct: () => {},
});

const ProductProvider: React.FunctionComponent = (props) => {
  const [competitorCompany, setCompetitorCompany] = useState({});
  const [competitorProduct, setCompetitorProduct] = useState<CompetitorProduct | null>(null);
  const [productReference, setProductReference] = useState<ProductReferenceResponse | null>({
    nart: "",
    productName: "",
    baseProduct: "",
  });
  const [productNartLevel, setProductNartLevel] = useState("");
  const [productObject, setProductObject] = useState(null);
  const [unknownProduct, updateUnknownProduct] = useState<UnknownProduct | null>(null);
  const [prototypeObject, setPrototypeObject] = useState<TesaPrototype>();
  const [laboratoryObject, setLaboratoryObject] = useState<Laboratory>();
  const [testRequest, setTestRequest] = useState({});
  const [tesaProductSamples, setTesaProductSamples] = useState([]);

  const updateCompetitorCompany = (company: CompetitorCompany) => {
    setCompetitorCompany(company);
  };

  const updateCompetitorProduct = (product: CompetitorProduct | null) => {
    setCompetitorProduct(product);
  };

  const updateProductReference = (newProductReference: ProductReferenceResponse | null) => {
    setProductReference(newProductReference);
  };

  const updateProductNartLevel = (newProductNartLevel: any) => {
    setProductNartLevel(newProductNartLevel);
  };

  const updateProductObject = (newProductObject: any) => {
    setProductObject(newProductObject);
  };

  const updatePrototypeObject = (newPrototypeObject: TesaPrototype) => {
    setPrototypeObject(newPrototypeObject);
  };

  const updateLaboratoryObject = (newLaboratoryObject: Laboratory) => {
    setLaboratoryObject(newLaboratoryObject);
  };

  const updateTesaProductSamples = (newTesaProductSamples: any) => {
    setTesaProductSamples(newTesaProductSamples);
  };

  const updateTestRequest = (newTestRequest: any) => {
    setTestRequest(newTestRequest);
  };

  const { children } = props;

  useEffect(() => {
    if (prototypeObject?.prototypeLaboratoryId) {
      setLaboratoryObject((prevLaboratoryObject) => {
        return {
          ...prevLaboratoryObject,
          name: prototypeObject.prototypeLaboratoryName,
          id: prototypeObject.prototypeLaboratoryId,
        };
      });
    }
  }, [prototypeObject]);

  return (
    <ProductContext.Provider
      value={{
        competitorCompany,
        competitorProduct,
        laboratoryObject,
        productReference,
        productNartLevel,
        productObject,
        prototypeObject,
        tesaProductSamples,
        testRequest,
        unknownProduct,
        updateCompetitorCompany,
        updateCompetitorProduct,
        updateLaboratoryObject,
        updateProductReference,
        updateProductNartLevel,
        updateProductObject,
        updatePrototypeObject,
        updateTesaProductSamples,
        updateTestRequest,
        updateUnknownProduct,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export default ProductProvider;
